import React, { useState } from "react";
import Select, { components } from "react-select";
import { Check } from "lucide-react";

// Global searchable dropdown primitive — same visual language as the
// react-select instance used in EntityFormModal.jsx (rounded surface,
// theme-token colors, no hover motion) so any page outside a CRUD form
// gets the identical premium select experience.
//
// Usage:
//   <SearchableSelect
//     options={[{ value: 1, label: "IST" }, ...]}
//     value={selectedId}
//     onChange={(val) => setSelectedId(val)}
//     placeholder="Select timezone"
//   />
//
// `checkboxes` (with `isMulti`) turns the menu into a checklist: each option
// has a checkbox, the menu stays open while ticking several, and the control
// shows the chosen labels as one line of text instead of chips.
const getStyles = (hasError, compact, checkboxes, hasValue) => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: compact ? "28px" : "38px",
    fontSize: "13px",
    borderWidth: "1px",
    borderRadius: "8px",
    backgroundColor: "var(--color-surface)",
    borderColor: hasError ? "var(--color-danger)" : state.isFocused ? "var(--color-border-strong)" : "var(--color-border)",
    boxShadow: state.isFocused ? (hasError ? "0 0 0 2px var(--color-danger-bg)" : "0 0 0 2px var(--color-border)") : "none",
    "&:hover": { borderColor: hasError ? "var(--color-danger)" : "var(--color-border-strong)" },
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: "13px",
    padding: "4px",
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "10px",
    overflow: "hidden",
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menuList: (provided) => ({ ...provided, padding: 0 }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "13px",
    marginBottom: "2px",
    borderRadius: "6px",
    padding: "8px 10px",
    backgroundColor:
      state.isSelected && !checkboxes ? "var(--color-primary-tint)" : state.isFocused ? "var(--color-surface-alt)" : "transparent",
    color: "var(--color-text-primary)",
    cursor: "pointer",
  }),
  singleValue: (provided) => ({ ...provided, fontSize: "13px", color: "var(--color-text-primary)" }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "13px",
    color: hasValue ? "var(--color-text-primary)" : "var(--color-text-muted)",
    ...(checkboxes ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}),
  }),
  input: (provided) => ({ ...provided, color: "var(--color-text-primary)", ...(compact ? { margin: 0, padding: 0 } : {}) }),
  valueContainer: (provided) => (compact ? { ...provided, padding: "0 6px" } : provided),
  dropdownIndicator: (provided) => (compact ? { ...provided, padding: 4 } : provided),
  clearIndicator: (provided) => (compact ? { ...provided, padding: 4 } : provided),
  indicatorSeparator: () => ({ display: "none" }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "var(--color-primary-tint)",
    borderRadius: "6px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "var(--color-primary)",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "var(--color-primary)",
    cursor: "pointer",
    ":hover": { backgroundColor: "transparent", color: "var(--color-danger)" },
  }),
});

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <span className="flex items-center gap-2.5 min-w-0">
      <span
        className={`flex-shrink-0 w-4 h-4 rounded-[4px] border flex items-center justify-center ${
          props.isSelected ? "bg-primary border-primary text-white" : "bg-surface border-border_strong"
        }`}
      >
        {props.isSelected && <Check size={11} strokeWidth={3} />}
      </span>
      <span className="truncate">{props.label}</span>
    </span>
  </components.Option>
);

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select...",
  isDisabled = false,
  isClearable = false,
  hasError = false,
  isLoading = false,
  isMulti = false,
  // Optional — used by inline grid editors (e.g. Estimate takeoff grid)
  compact = false,
  autoFocus = false,
  defaultMenuIsOpen = false,
  onMenuClose,
  onKeyDown,
  ariaLabel,
  checkboxes = false,
}) {
  const isChecklist = isMulti && checkboxes;
  // Checklist mode keeps the typed search after each tick, so several matches
  // can be checked without retyping; it clears when the menu closes.
  const [inputValue, setInputValue] = useState("");

  const selected = isMulti
    ? options.filter((o) => (Array.isArray(value) ? value : []).some((v) => String(v) === String(o.value)))
    : options.find((o) => String(o.value) === String(value)) || null;

  const checklistSummary = isChecklist && selected.length ? selected.map((o) => o.label).join(", ") : null;

  const handleChange = (opt) => {
    if (isMulti) {
      onChange((opt || []).map((o) => o.value));
    } else {
      onChange(opt?.value ?? "");
    }
  };

  return (
    <Select
      options={options}
      value={selected}
      onChange={handleChange}
      placeholder={checklistSummary || placeholder}
      isSearchable
      isMulti={isMulti}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isLoading={isLoading}
      styles={getStyles(hasError, compact, isChecklist, !!checklistSummary)}
      {...(isChecklist && {
        components: { Option: CheckboxOption },
        closeMenuOnSelect: false,
        // react-select blurs after each pick on touch-capable devices, which would close the menu.
        blurInputOnSelect: false,
        hideSelectedOptions: false,
        controlShouldRenderValue: false,
        backspaceRemovesValue: false,
        inputValue,
        onInputChange: (text, { action }) => {
          if (action !== "set-value") setInputValue(text);
        },
      })}
      autoFocus={autoFocus}
      defaultMenuIsOpen={defaultMenuIsOpen}
      onMenuClose={onMenuClose}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
    />
  );
}
